const API_BASE_URL = process.env.REACT_APP_API_URL;

function getSessionToken() {
  try {
    const stored = JSON.parse(localStorage.getItem("dealer") || "null");
    return stored?.sessionToken || null;
  } catch (_) {
    return null;
  }
}

function handleSessionExpired() {
  localStorage.removeItem("dealer");
  window.location.href = "/login";
}

// Attaches the bearer token and redirects to /login on NO_SESSION/SESSION_EXPIRED.
// There is no refresh flow — an expired session always means log in again.
async function authFetch(url, options = {}) {
  const token = getSessionToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401) {
    let errorCode = null;
    try {
      const data = await response.clone().json();
      errorCode = data?.error;
    } catch (_) {
      // no JSON body
    }

    if (errorCode === "NO_SESSION" || errorCode === "SESSION_EXPIRED") {
      handleSessionExpired();
    }
  }

  return response;
}


export async function fetchAuthCapabilities() {
  const response = await fetch(`${API_BASE_URL}/api/auth/capabilities`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to load login options");
  }
  return data;
}

export async function requestOtp(mobile) {
  const response = await fetch(`${API_BASE_URL}/api/auth/request-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mobile }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || "Unable to send OTP");
    error.retryAfter = data.retryAfter || null;
    throw error;
  }
  return data;
}

export async function verifyOtp(requestId, otp) {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ requestId, otp }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to verify OTP");
  }
  return data;
}

export async function selectOtpAccount(selectionToken, accountUniqueCode) {
  const response = await fetch(`${API_BASE_URL}/api/auth/select-account`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selectionToken, accountUniqueCode }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to complete login");
  }
  return data;
}

export async function loginUser(username, password) {
  const response = await fetch(`${API_BASE_URL}/api/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Login failed");
  }

  return data;
}

export async function authenticateWithWaToken(token) {
  const response = await fetch(`${API_BASE_URL}/api/auth/wa-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token }),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || "Sign-in failed");
    error.code = data.error || null;
    throw error;
  }

  return data;
}

export async function logoutUser() {
  const response = await authFetch(`${API_BASE_URL}/api/session`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error("Logout failed");
  }

  return response.json();
}


export async function fetchPortalProfile() {
  const response = await authFetch(`${API_BASE_URL}/api/portal/me`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to load portal profile");
  }
  return data;
}

export async function fetchPortalContacts() {
  const response = await authFetch(`${API_BASE_URL}/api/portal/contacts`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to load staff access");
  }
  return data;
}

export async function addPortalContact(payload) {
  const response = await authFetch(`${API_BASE_URL}/api/portal/contacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to add staff contact");
  }
  return data;
}

export async function updatePortalContact(id, payload) {
  const response = await authFetch(`${API_BASE_URL}/api/portal/contacts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to update staff contact");
  }
  return data;
}

export async function deletePortalContact(id) {
  const response = await authFetch(`${API_BASE_URL}/api/portal/contacts/${id}`, {
    method: "DELETE",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to remove staff contact");
  }
  return data;
}

export async function fetchOrdersData() {
  const response = await authFetch(`${API_BASE_URL}/api/orders-data`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch orders data");
  }

  return data;
}


export async function cancelOnlineOrder(orderId) {
  const response = await authFetch(
    `${API_BASE_URL}/api/orders-data/online/${encodeURIComponent(orderId)}/cancel`,
    { method: "PATCH" }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || "Unable to cancel this order.");
    error.status = response.status;
    throw error;
  }

  return data;
}


export async function fetchAccountMonthlyAnalysis() {
  const response = await authFetch(`${API_BASE_URL}/api/account-monthly-analysis`);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch monthly account analysis");
  }

  return data;
}

export async function fetchAccountSummary() {
  const response = await authFetch(`${API_BASE_URL}/api/account-summary`);

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch account summary");
  }

  return data;
}

export async function fetchCatalogueDispatchHistory() {
  const response = await authFetch(`${API_BASE_URL}/api/catalogue-dispatch-history`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Unable to load dispatch history");
  return data;
}

export async function fetchCataloguePerformance() {
  const response = await authFetch(`${API_BASE_URL}/api/catalogue-performance`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Unable to load catalogue performance");
  return data;
}

export async function fetchMonthlySalesPerformance() {
  const response = await authFetch(`${API_BASE_URL}/api/sales-performance`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Unable to load sales performance");
  return data;
}

export async function fetchStockCategories() {
  const response = await authFetch(`${API_BASE_URL}/api/stock/categories`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch stock categories");
  }

  return data;
}

export async function fetchStockItems() {
  const response = await authFetch(`${API_BASE_URL}/api/stock/items`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to fetch stock items");
  }

  return data;
}

export async function fetchLedgerPdf(fromDate, toDate) {
  const params = new URLSearchParams({ fromDate, toDate });
  const response = await authFetch(`${API_BASE_URL}/api/ledger?${params.toString()}`);

  if (!response.ok) {
    let message = "Unable to generate ledger right now. Please try again in a moment.";
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
    } catch (_) {
      // response body wasn't JSON; keep the default message
    }
    throw new Error(message);
  }

  return response.blob();
}

function filenameFromContentDisposition(headerValue) {
  if (!headerValue) return null;
  const match = /filename="?([^";]+)"?/i.exec(headerValue);
  return match ? match[1] : null;
}

export async function fetchInvoicePdf(vchCode) {
  const params = new URLSearchParams({ vchCode });
  const response = await authFetch(`${API_BASE_URL}/api/files/invoice?${params.toString()}`);

  if (!response.ok) {
    let message = "Unable to download invoice right now. Please try again in a moment.";
    let code = null;
    try {
      const data = await response.json();
      code = data?.error || null;
      if (data?.message) message = data.message;
    } catch (_) {
      // response body wasn't JSON; keep the default message
    }
    const error = new Error(message);
    error.status = response.status;
    error.code = code;
    throw error;
  }

  const filename = filenameFromContentDisposition(response.headers.get("Content-Disposition"));
  const blob = await response.blob();
  return { blob, filename };
}



export async function submitOnlineOrder(payload) {
  const response = await authFetch(`${API_BASE_URL}/api/stock/orders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to submit order request");
  }
  return data;
}


export async function getPortalPasswordStatus() {
  const response = await authFetch(`${API_BASE_URL}/api/auth/password-status`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || "Unable to load password settings");
  return data;
}

export async function setPortalPassword(password) {
  const response = await authFetch(`${API_BASE_URL}/api/auth/password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || "Unable to save password");
    error.code = data.code;
    throw error;
  }
  return data;
}

export async function fetchDealerMessages() {
  const response = await authFetch(`${API_BASE_URL}/api/messages`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || "Unable to load messages");
  return data;
}

export async function sendDealerMessage(body, attachments = []) {
  const response = await authFetch(`${API_BASE_URL}/api/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body, attachments }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || "Unable to send message");
  return data;
}
